import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
	ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, of, Subject, take, takeUntil } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';
import { LegacyApiService } from '@lmc-app/core/services/legacy-api.service';
import { selectUser } from '@lmc-app/store';
import { User } from '@lmc-app/shared/interface/user.interface';
import { MatDialog } from '@angular/material/dialog';
import { PandadocPreviewModalComponent } from './pandadoc-preview-modal/pandadoc-preview-modal.component';

@Component({
	selector: 'app-pandadoc',
	templateUrl: './pandadoc.component.html',
	styleUrls: ['./pandadoc.component.scss'],
	encapsulation: ViewEncapsulation.Emulated,
})
export class PandadocComponent implements OnInit, OnChanges, OnDestroy {
	currentRoute: string;
	currentZohoAppIdFromUrl: string;
	@Input() application: any;
	@Output() applicationChange = new EventEmitter<any>();
	pandadoc_documents: any[] = [];
	readonly tableHeaders = ['name', 'status', 'dateCreated', 'dateModified', 'preview'];
	isLoading = true;
	hasError = false;
	errorMessage = '';
	private destroy$ = new Subject<void>();
	private user: User = null;

	constructor(
		private store: Store,
		private apiService: LegacyApiService,
		private activatedRoute: ActivatedRoute,
		private dialog: MatDialog
	) {}

	// Getter methods for template
	get showStateDisplay(): boolean {
		return (
			this.isLoading || this.hasError || this.pandadoc_documents.length === 0
		);
	}

	get stateType(): 'loading' | 'empty' | 'error' {
		if (this.isLoading) return 'loading';
		if (this.hasError) return 'error';
		return 'empty';
	}

	get showTable(): boolean {
		return (
			!this.isLoading && !this.hasError && this.pandadoc_documents.length > 0
		);
	}

	ngOnInit(): void {
		this.activatedRoute.url.subscribe((event) => {
			this.currentRoute = event[0].path;
		});

		this.activatedRoute.pathFromRoot[1].url.subscribe((val) => {
			this.currentZohoAppIdFromUrl = val?.[2]?.path;
		});

		this.store
			.select(selectUser)
			.pipe(
				filter((user) => !!user),
				take(1),
				takeUntil(this.destroy$)
			)
			.subscribe((user) => {
				this.user = user;
			});
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes.application?.currentValue) {
			this.application = changes.application?.currentValue;
			this.loadDocuments();
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	loadDocuments() {
		this.isLoading = true;
		this.hasError = false;
		this.errorMessage = '';
		this.pandadoc_documents = [];

		this.apiService
			.ListDocumentsPandadocs(this.application.id)
			.pipe(
				timeout(30000), // 30 second timeout
				catchError((error) => {
					console.error('Pandadoc API error or timeout:', error);
					return of({ results: [] });
				})
			)
			.subscribe({
				next: (res: any) => {
					// Check if results exist and has documents
					if (
						res?.results &&
						Array.isArray(res.results) &&
						res.results.length > 0
					) {
						this.pandadoc_documents = res.results;
					} else {
						this.pandadoc_documents = [];
					}

					this.isLoading = false;
				},
				error: (err) => {
					console.error('Error loading Pandadoc documents:', err);
					this.pandadoc_documents = [];
					this.hasError = true;
					this.errorMessage = 'Error loading documents';
					this.isLoading = false;
				},
			});
	}

	createRequest() {
		window.open(
			`https://app.pandadoc.com/a/#/documents/import-new-with-folders?payload={"id": "${this.application.id}", "type": "potential", "provider_user_id": "${this.user.zoho_owner_id}"}&provider=zohocrm`,
			'_blank'
		);
	}

	refreshDocuments() {
		this.loadDocuments();
	}

	openPreview(doc: any) {
		this.dialog.open(PandadocPreviewModalComponent, {
			maxHeight: '90vh',
			width: '80vw',
			maxWidth: '95vw',
			position: { top: '40px' },
			autoFocus: false,
			panelClass: 'hide-dialog-scrollbar',
			data: { id: doc.id, name: doc.name, status: doc.status },
		});
	}
}
